import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Header } from "./components/Header";
import styles from "./MilestonesSummary.module.scss";

import {
  getCountByCompleted,
  getCountByType,
} from "../../../api/milestonesSummary/milestonesSummary";
import { useUserData } from "../../../hooks/useUserProfile";
import { useAppSelector } from "../../../store/hooks/hooks";
import {
  selectCompleted,
  selectFinishFrom,
  selectMilestoneType,
  selectFinishTo,
  selectProjects,
} from "../../../store/slices/reportsSlice/milestonesSlice";
import { DataItem } from "../../../types/previewTemplate";
import { Loader } from "../../Loader";
import { PieChart } from "../PieChart";
import { VerticalBarChart } from "../VerticalBarChart";

export const MilestonesSummary = () => {
  const { syncLogId } = useParams();

  const projects = useAppSelector(selectProjects);
  const milestoneType = useAppSelector(selectMilestoneType);
  const completed = useAppSelector(selectCompleted);
  const periodFrom = useAppSelector(selectFinishFrom);
  const periodTo = useAppSelector(selectFinishTo);

  const { organizationId, tenantId } = useUserData();
  const { reportId } = useParams();

  const queryEnabled =
    !!tenantId &&
    !!reportId &&
    !!syncLogId &&
    !!projects &&
    !!milestoneType &&
    !!completed &&
    !!periodFrom &&
    !!periodTo;

  const { data: verticalBar, isLoading: isVerticalBarLoading } = useQuery({
    queryKey: [
      "getMilestonesChartData",
      "getCountByType",
      syncLogId,
      projects,
      milestoneType,
      completed,
      periodFrom,
      periodTo,
    ],
    queryFn: async () => {
      const verticalBarByType = await getCountByType(
        syncLogId!,
        organizationId,
        tenantId,
        projects,
        milestoneType,
        completed,
        periodFrom,
        periodTo
      );
      
      return verticalBarByType.data.data;
    },
    enabled: queryEnabled
  });

  const { data: pieChart, isLoading: isPieChartLoading } = useQuery({
    queryKey: [
      "getMilestonesChartData",
      "getCountByCompleted",
      syncLogId,
      projects,
      milestoneType,
      completed,
      periodFrom,
      periodTo,
    ],
    queryFn: async () => {
      const pieChartByCompleted = await getCountByCompleted(
        syncLogId!,
        organizationId,
        tenantId,
        projects,
        milestoneType,
        completed,
        periodFrom,
        periodTo
      );

      return pieChartByCompleted.data.data;
    },
    enabled: queryEnabled
  });

  const { maxDataValue, padding } = useMemo(() => {
    if (verticalBar?.data?.length > 0) {
      const maxValue = Math.max(...verticalBar.data.map((item: DataItem) => item.value));
      const paddingValue = maxValue * 0.1;
      return { maxDataValue: maxValue, padding: paddingValue };
    }
    return { maxDataValue: 0, padding: 0 };
  }, [verticalBar]);

  const isLoading = isVerticalBarLoading || isPieChartLoading;

  return (
    <div className={styles.root}>
      <Header />
      {isLoading && <Loader />}
      {!isLoading && (
        <div className={styles.wrapper}>
          <PieChart
            dataList={pieChart?.data}
            title={pieChart?.title}
            legendTitle={pieChart?.legendTitle}
          />
          <VerticalBarChart
            dataList={verticalBar?.data}
            options={{
              responsive: true,
              plugins: {
                datalabels: {
                  color: "black",
                  labels: {
                    title: {
                      font: {
                        weight: 400,
                        size: 12,
                      },
                    },
                  },
                  anchor: "end",
                  align: -90,
                },
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  max: maxDataValue + padding,
                  ticks: {
                    stepSize: 2,
                  },
                  title: {
                    display: true,
                    text: verticalBar?.y,
                    color: "#9E9E9E",
                    font: {
                      size: 14,
                      weight: 400,
                    },
                    padding: {
                      bottom: 15,
                    },
                  },
                },

                x: {
                  title: {
                    display: true,
                    text: verticalBar?.x,
                    color: "#9E9E9E",
                    font: {
                      size: 14,
                      weight: 400,
                    },
                    padding: {
                      top: 20,
                    },
                  },
                },
              },
              clip: false,
            }}
            title={verticalBar?.title}
          />
        </div>
      )}
    </div>
  );
};
